import { motion } from 'framer-motion';
import { Flame, Zap, Star } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import useReducedMotion from '../../hooks/useReducedMotion';
import { fadeUp, flamePulse, maybe } from '../../lib/motionVariants';

/**
 * GreetingBar — "Panel" redesign top block: welcome heading + subtitle on the
 * left, 3 stat chips (streak / XP / badge count) on the right.
 *
 * Props:
 *   user       — profile object (name/full_name/email)
 *   streak     — number
 *   totalXp    — number
 *   badgeCount — number of earned badges (0 while badge data isn't wired to a real endpoint)
 */
export default function GreetingBar({ user, streak = 0, totalXp = 0, badgeCount = 0 }) {
  const { t } = useLanguage();
  const reduced = useReducedMotion();

  const name =
    user?.name ||
    user?.full_name ||
    (user?.email ? user.email.split('@')[0] : t('dashboard.defaultName'));

  const greeting = t('dashboard.duo.greeting').replace('{{name}}', name);

  return (
    <motion.div
      variants={maybe(reduced, fadeUp)}
      initial="hidden"
      animate="visible"
      className="flex flex-wrap items-center justify-between gap-4 mb-5"
      data-testid="greeting-bar"
    >
      {/* Left: greeting + subtitle */}
      <div className="min-w-0">
        <h1 className="font-duo-display text-2xl text-[#0E1F3D] truncate">
          {greeting}
        </h1>
        <p className="text-sm text-[#6b7a70] mt-0.5">
          {t('dashboard.duo.continueLearning')}
        </p>
      </div>

      {/* Right: stat chips */}
      <div className="flex items-center gap-2.5 shrink-0">
        {/* Streak chip */}
        <span
          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border bg-[#fff8e8] border-[#f0e2b8] text-sm font-bold"
          aria-label={`${streak} ${t('dashboard.duo.streakLabel')}`}
          data-testid="stat-chip-streak"
        >
          <motion.span
            variants={maybe(reduced, flamePulse)}
            animate={reduced ? {} : 'animate'}
            aria-hidden="true"
            className="flex items-center"
          >
            <Flame size={15} strokeWidth={2.5} className="text-amber-500" />
          </motion.span>
          <b className="text-[#b58a12]">{streak}</b>
          <span className="text-xs font-semibold text-[#b58a12]">
            {t('dashboard.duo.streakUnit')}
          </span>
        </span>

        {/* XP chip */}
        <span
          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border bg-[#eaf4fb] border-[#cfe4f2] text-sm font-bold"
          aria-label={`${totalXp} ${t('dashboard.duo.xpLabel')}`}
          data-testid="stat-chip-xp"
        >
          <Zap size={15} strokeWidth={2.5} className="text-[#2c7bb0]" aria-hidden="true" />
          <b className="text-[#2c7bb0]">{totalXp.toLocaleString()}</b>
          <span className="text-xs font-semibold text-[#2c7bb0]">
            {t('dashboard.duo.xpUnit')}
          </span>
        </span>

        {/* Badge count chip */}
        <span
          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border bg-[#eaf8ef] border-[#c7ead5] text-sm font-bold"
          aria-label={`${badgeCount} ${t('dashboard.duo.badgeUnit')}`}
          data-testid="stat-chip-badges"
        >
          <Star size={15} strokeWidth={2.5} className="text-[#16794a]" aria-hidden="true" />
          <b className="text-[#16794a]">{badgeCount}</b>
          <span className="text-xs font-semibold text-[#16794a]">
            {t('dashboard.duo.badgeUnit')}
          </span>
        </span>
      </div>
    </motion.div>
  );
}
